import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom Hook: useAPI
 * Manages API calls, loading, and error states
 * @template T - API response data type
 * @returns API execution function and state
 */
export function useAPI<T = any>() {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const execute = useCallback(
    async (promise: Promise<any>) => {
      try {
        setLoading(true);
        setError(null);
        const response = await promise;

        // Handle auth errors
        if (response?.status === 401) {
          localStorage.clear();
          navigate('/login');
          return null;
        }

        // Handle success
        if (response?.status === 200 || response?.status === 201) {
          const result = response.data?.data;
          setData(result);
          return result;
        }

        // Handle errors
        const errorMsg = response?.data?.error?.message || 'An error occurred';
        setError(errorMsg);
        return null;
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.error?.message ||
          err?.message ||
          'An error occurred';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  return { data, loading, error, execute };
}
